import axios from 'axios'
import { apiBaseUrl, isDev, config } from '@/config/config'
import csrfService from './csrf'
import i18n from '@/i18n'
import Vue from 'vue'

const successCode = config.successCode

// 是否已经弹出过安全验证提示，避免多个并发请求重复弹框
let challengeDialogShown = false

/**
 * 判断响应是否为 Cloudflare 的安全挑战页
 * Under Attack / Managed Challenge 过期后，接口会返回挑战 HTML 而非正常 JSON，
 * 特征：cf-mitigated: challenge 响应头，或 403/503 且 content-type 为 text/html
 */
const isCloudflareChallenge = (response) => {
  if (!response) return false
  const headers = response.headers || {}
  if (headers['cf-mitigated'] === 'challenge') return true
  const contentType = headers['content-type'] || ''
  const status = response.status
  const isHtml = contentType.includes('text/html') || typeof response.data === 'string'
  return (status === 403 || status === 503) && isHtml
}

/**
 * 安全验证过期处理：XHR 无法自行解挑战，引导用户刷新页面由浏览器重新完成验证
 */
const handleCloudflareChallenge = () => {
  if (challengeDialogShown) return
  challengeDialogShown = true
  Vue.prototype.$message.error({
    message: i18n.t('common.securityCheckExpired'),
    duration: 0, // 不自动消失
    showClose: true,
    actions: [
      {
        text: i18n.t('common.refreshPage'),
        type: 'primary',
        handler: () => {
          window.location.reload()
        }
      }
    ]
  })
}

// 会话失效时清理本地登录态缓存，并通知页面上已挂载的组件（如 HeaderNav）同步更新
const clearLoginState = () => {
  localStorage.removeItem('user_id')
  localStorage.removeItem('username')
  window.dispatchEvent(new Event('auth:expired'))
}

// 请求失败后的错误统一处理
const netErrorHandle = (status, errorCode, msg) => {
  switch (status) {
    case 401:
      clearLoginState()
      if (msg) {
        console.error(msg)
        // 401错误显示不自动消失的弹框，带重新登录按钮
        Vue.prototype.$message.error({
          message: msg,
          duration: 0, // 不自动消失
          showClose: true,
          actions: [
            {
              text: i18n.t('common.reLogin'),
              type: 'primary',
              handler: () => {
                // 跳转到登录页面
                const currentPath = window.location.pathname + window.location.search
                window.location.href = `/#/login?redirect=${encodeURIComponent(currentPath)}`
              }
            }
          ]
        })
      } else {
        const defaultMsg = i18n.t('common.loginExpired')
        console.error(defaultMsg)
        Vue.prototype.$message.error({
          message: defaultMsg,
          duration: 0, // 不自动消失
          showClose: true,
          actions: [
            {
              text: i18n.t('common.reLogin'),
              type: 'primary',
              handler: () => {
                 // 跳转到登录页面
                 const currentPath = window.location.pathname + window.location.search
                 window.location.href = `/#/login?redirect=${encodeURIComponent(currentPath)}`
               }
            }
          ]
        })
      }
      break
    default:
      if (msg) {
        console.error(msg)
        Vue.prototype.$message.error(msg)
      } else {
        const defaultMsg = `服务器异常: ${status}`
        console.error(defaultMsg)
        Vue.prototype.$message.error(defaultMsg)
      }
  }
}

// 创建axios实例
const instance = axios.create({
  baseURL: apiBaseUrl,
  timeout: config.timeout,
  withCredentials: config.withCredentials
})

/**
 * 请求拦截器
 * 每次请求前，如果存在token则在请求头中携带token
 */
instance.interceptors.request.use(
  (config) => {
    // 从 Cookie 中获取 CSRF token（页面加载时已确保存在）
    const csrfToken = csrfService.getToken()
    
    // 添加 CSRF token 到请求头
    if (csrfToken) {
      config.headers.Csrf = csrfToken
    }
    
    // 添加国际化locale到请求头
    config.headers['X-Trangpt-Locale'] = i18n.locale
    
    // 在开发环境下添加dev参数到header中
    if (isDev) {
      config.headers.Dev = 'true'
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    // 挑战过期时可能返回 200 + HTML，此时 response.data 不是正常 JSON
    if (isCloudflareChallenge(response)) {
      handleCloudflareChallenge()
      return Promise.reject(response.data)
    }
    if (successCode.indexOf(response.data.statusCode) > -1) {
      return Promise.resolve(response.data)
    } else {
      return Promise.reject(response.data)
    }
  },
  (error) => {
    const { response, message: msg } = error
    if (response) {
      // 挑战过期通常以 403/503 返回，优先识别，避免误报“服务器异常”
      if (isCloudflareChallenge(response)) {
        handleCloudflareChallenge()
        return Promise.reject(response.data)
      }
      netErrorHandle(response.status, response.data?.error, response.data?.message)
      return Promise.reject(response.data)
    } else {
      if (msg && msg.indexOf('timeout') > -1) {
        const timeoutMsg = '请求超时，请稍后重试'
        console.error(timeoutMsg)
        Vue.prototype.$message.error(timeoutMsg)
        return Promise.reject(error)
      } else if (!window.navigator.onLine) {
        // 通知断网
        const networkMsg = '网络异常'
        console.error(networkMsg)
        Vue.prototype.$message.error(networkMsg)
        return Promise.reject(error)
      } else {
        // 其他未知错误
        const unknownMsg = '请求失败，请稍后重试'
        console.error('未知错误:', error)
        Vue.prototype.$message.error(unknownMsg)
        return Promise.reject(error)
      }
    }
  }
)

export default instance